import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Invoice as a Coach in the UK — VAT, HMRC & Getting Paid',
      'description': 'A complete guide for UK coaches: what to put on an invoice, VAT rules for coaching services, HMRC self-assessment, and how to get paid faster.',
      'url': 'https://www.invilic.com/blog/how-to-invoice-as-a-coach-uk',
      'datePublished': '2026-06-07',
      'dateModified': '2026-06-07',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Do coaches have to charge VAT in the UK?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Only if your taxable turnover exceeds the VAT registration threshold, which is £90,000 in any rolling 12-month period (as of 2024). Below this threshold, you do not charge VAT and your invoice total is the full amount due. Once you exceed £90,000, you must register with HMRC and charge 20% VAT on coaching services.' },
        },
        {
          '@type': 'Question',
          'name': 'Are health coaching services VAT exempt in the UK?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Only if the coaching qualifies as medical or health services provided by a regulated health professional — for example, a physiotherapist or registered nurse providing health coaching. Life coaching, business coaching, executive coaching, and general wellbeing coaching are all standard-rated at 20% VAT if you are VAT registered. There is no blanket coaching exemption from VAT.' },
        },
        {
          '@type': 'Question',
          'name': 'Do UK coaches need to do Self Assessment?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, if you are self-employed or a sole trader earning more than £1,000 from coaching in a tax year, you must register for Self Assessment with HMRC and file an annual tax return. You pay income tax and Class 4 National Insurance on your profits. Register by 5 October following the end of the tax year in which you started coaching.' },
        },
        {
          '@type': 'Question',
          'name': 'What information must a UK coach put on an invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A UK coaching invoice must include: your full name or trading name and address, the client name and address, a unique sequential invoice number, the invoice date, a description of the coaching service, the amount due (and VAT breakdown if VAT registered), your payment terms, and your bank details. If VAT registered, also include your VAT registration number.' },
        },
        {
          '@type': 'Question',
          'name': 'What payment terms should a UK coach use?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Most independent UK coaches use payment upfront, Net 7, or Net 14 terms. For ongoing coaching programmes, monthly invoices issued on the same date each month work well. For one-off workshops or corporate coaching days, invoicing immediately after delivery with Net 30 terms is standard for corporate clients.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const invoiceFields = [
  { field: 'Your full name or trading name', note: 'Sole traders use their legal name; limited companies use the registered company name' },
  { field: 'Your business address and email', note: 'Required on all UK business invoices' },
  { field: "Client's name and billing address", note: 'Full name and address of the person or company you are coaching' },
  { field: 'A unique sequential invoice number', note: 'e.g. INV-2026-001 — must be sequential and never reused' },
  { field: 'Invoice date', note: 'The date you are issuing the invoice' },
  { field: 'Description of coaching services', note: 'e.g. "Executive coaching — 3 sessions, May 2026, 60 min each"' },
  { field: 'Fee per session or programme total', note: 'Break down per session or state the total package fee clearly' },
  { field: 'Payment due date', note: 'e.g. "Payment due within 14 days of invoice date"' },
  { field: 'Your bank details', note: 'Sort code + account number, or a payment link if you use Stripe/GoCardless' },
  { field: 'VAT registration number and VAT amount', note: 'Only required if your turnover exceeds £90,000 — omit entirely if not VAT registered' },
];

export default function HowToInvoiceAsCoachUkPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Invoice as a Coach in the UK — VAT, HMRC & Getting Paid | Invilic Blog"
        description="Complete guide for UK coaches: what to include on a coaching invoice, VAT rules for coaching services, HMRC self-assessment, and how to get paid faster."
        canonical="https://www.invilic.com/blog/how-to-invoice-as-a-coach-uk"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">UK Invoicing</span>
          </div>
          <span className="inline-block bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">UK Invoicing</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Invoice as a Coach in the UK — VAT, HMRC & Getting Paid
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 7, 2026 · 7 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            As a self-employed coach in the UK, your invoice is both a payment request and a legal document. Get it right from day one — know what HMRC requires, understand when you need to charge VAT, and use the payment terms that actually get you paid on time.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What to Include on a UK Coaching Invoice</h2>
          <p className="mb-4">HMRC does not prescribe an exact format for non-VAT-registered businesses, but these fields are required or strongly recommended:</p>
          <div className="space-y-3 mb-8">
            {invoiceFields.map(({ field, note }, i) => (
              <div key={field} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</div>
                <div className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{field}</span>
                  {note && <p className="text-gray-400 dark:text-gray-500 mt-0.5">{note}</p>}
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Create a professional UK coaching invoice in minutes — all required fields, instant PDF, no account needed."
            to="/coach-invoice-generator"
            btnText="Create Coach Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">VAT on Coaching Services in the UK</h2>
          <p className="mb-4">
            Coaching services are standard-rated at <strong className="font-semibold text-gray-900 dark:text-white">20% VAT</strong> in the UK. This applies to life coaching, business coaching, executive coaching, career coaching, and personal development coaching.
          </p>
          <p className="mb-4">
            The critical rule: you only need to charge VAT once your taxable turnover exceeds <strong className="font-semibold text-gray-900 dark:text-white">£90,000</strong> in any rolling 12-month period.
          </p>
          <div className="space-y-3 mb-6">
            {[
              { label: 'Below £90,000 turnover', detail: 'You are not VAT registered. Do not add VAT to your invoices. Your coaching fee is the full amount due — no need to mention VAT at all.' },
              { label: 'Above £90,000 turnover', detail: 'You must register for VAT with HMRC within 30 days. Add 20% VAT to all coaching fees. Your invoice must clearly show the net amount, VAT amount, and gross total separately.' },
              { label: 'Voluntary VAT registration', detail: 'You can register voluntarily even below the threshold. This is useful if your clients are VAT-registered businesses — they can reclaim the VAT, making your services no more expensive to them.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl text-sm">
                <p className="font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
                <p className="text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl mb-8 text-sm">
            <strong className="font-semibold text-gray-900 dark:text-white">Health coaching exception:</strong> Regulated health professionals (e.g., registered nurses, physiotherapists, psychologists) providing therapeutic health services may qualify for VAT exemption under Group 7 of Schedule 9 of the VAT Act. Pure life coaching and wellbeing coaching remain standard-rated. Check HMRC VAT Notice 701/57 or speak to your accountant.
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">HMRC Self Assessment for UK Coaches</h2>
          <p className="mb-4">
            If you earn more than <strong className="font-semibold text-gray-900 dark:text-white">£1,000</strong> from self-employed coaching in a tax year (April to April), you must register for Self Assessment and file an annual tax return.
          </p>
          <ul className="space-y-2 mb-8 text-sm">
            {[
              'Register by 5 October following the first tax year you start coaching — e.g., if you started April 2025, register by 5 October 2025',
              'File your Self Assessment return online by 31 January each year for the previous April–March tax year',
              'Pay income tax on coaching profits: 20% (basic rate up to £50,270), 40% (higher rate), 45% (additional rate above £125,140)',
              'Pay Class 4 National Insurance on profits above £12,570',
              'Keep all invoices and financial records for at least 5 years after the filing deadline',
            ].map(item => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Sole Trader vs. Limited Company — What It Means for Your Invoice</h2>
          <p className="mb-4">Most independent coaches operate as sole traders. Here is how the structure affects what goes on your invoice:</p>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                title: 'Sole trader',
                points: [
                  'Invoice in your own legal name or registered trading name',
                  'No company registration number required',
                  'Pay income tax through Self Assessment',
                  'Simpler admin — best for most coaches starting out',
                ],
                color: 'violet',
              },
              {
                title: 'Limited company',
                points: [
                  'Invoice in the company name',
                  'Must show company registration number and registered address',
                  'Pay corporation tax on profits',
                  'More tax-efficient above approximately £50,000 annual profit',
                ],
                color: 'blue',
              },
            ].map(({ title, points, color }) => (
              <div key={title} className={`p-5 rounded-xl border border-${color}-100 dark:border-${color}-800/30 bg-${color}-50 dark:bg-${color}-900/10`}>
                <p className={`font-bold text-${color}-700 dark:text-${color}-300 mb-3 text-sm`}>{title}</p>
                <ul className="space-y-2">
                  {points.map(p => (
                    <li key={p} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0 mt-0.5`} /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Payment Terms for UK Coaches</h2>
          <p className="mb-4">Choosing the right payment terms is the most practical thing you can do to reduce late payments:</p>
          <div className="space-y-3 mb-8">
            {[
              { model: 'Single session coaching', terms: 'Payment upfront or Due on Receipt — remove the risk entirely for one-off sessions' },
              { model: 'Programme (e.g. 6-session block)', terms: '50% deposit upfront, 50% on first session — or invoice each session in advance' },
              { model: 'Monthly retainer coaching', terms: 'Invoice on the 1st of each month for that month — direct debit via GoCardless works best' },
              { model: 'Corporate coaching day', terms: 'Net 30 is standard for corporate clients — invoice immediately after delivery' },
            ].map(({ model, terms }) => (
              <div key={model} className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 text-sm">
                <p className="font-bold text-gray-900 dark:text-white mb-1">{model}</p>
                <p className="text-gray-500 dark:text-gray-400">{terms}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a UK coaching invoice in 2 minutes — VAT-ready format, sequential numbering, instant PDF. No account required."
            to="/coach-invoice-generator"
            btnText="Create UK Coach Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/how-to-invoice-as-a-coach-uk" />
      </main>
    </div>
  );
}
